import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import CurrencyInput from '../components/CurrencyInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatCurrency, getStatusLabel } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  DoorOpen,
  User,
  Wifi,
  Car,
  Droplets,
  Zap,
  Wind,
  Tv,
  Upload,
  Image,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const facilityIcons = {
  'WiFi': Wifi,
  'Parkir': Car,
  'Kamar Mandi Dalam': Droplets,
  'AC': Wind,
  'TV': Tv,
  'Listrik': Zap,
};

const defaultFacilities = ['WiFi', 'Parkir', 'Kamar Mandi Dalam', 'AC', 'TV', 'Listrik'];

const RoomCard = ({ room, onEdit, onDelete }) => {
  const { isPemilik } = useAuth();

  return (
    <Card className="room-card" data-testid={`room-card-${room.id}`}>
      <div className="relative">
        <img
          src={room.image_url || 'https://images.unsplash.com/photo-1614635884840-85cf80d23844?crop=entropy&cs=srgb&fm=jpg&q=85'}
          alt={room.name}
          className="room-card-image"
        />
        <Badge className={`absolute top-3 left-3 status-${room.status}`}>
          {getStatusLabel(room.status)}
        </Badge>
        {isPemilik() && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white"
                data-testid={`room-menu-${room.id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(room)} data-testid={`edit-room-${room.id}`}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(room)}
                className="text-destructive"
                data-testid={`delete-room-${room.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg">{room.name}</h3>
            <p className="text-sm text-muted-foreground">Lantai {room.floor}</p>
          </div>
          <p className="font-heading font-bold text-primary">
            {formatCurrency(room.price)}
            <span className="text-xs font-normal text-muted-foreground">/bln</span>
          </p>
        </div>

        {room.tenant_name && (
          <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-blue-50 text-blue-700">
            <User className="w-4 h-4" />
            <span className="text-sm font-medium">{room.tenant_name}</span>
          </div>
        )}

        {room.facilities?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {room.facilities.map((facility, i) => {
              const Icon = facilityIcons[facility] || DoorOpen;
              return (
                <div key={i} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  <Icon className="w-3 h-3" />
                  <span>{facility}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RoomForm = ({ room, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: room?.name || '',
    floor: room?.floor || 1,
    price: room?.price || '',
    facilities: room?.facilities || [],
    description: room?.description || '',
    image_url: room?.image_url || ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipe file tidak didukung. Gunakan JPG, PNG, atau WebP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/room-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // If URL starts with http, use as-is (GCS), otherwise prepend API_URL
        const imageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${API_URL}${response.data.url}`;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Gambar berhasil diupload');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengupload gambar');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const toggleFacility = (facility) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...prev.facilities, facility]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Mohon isi nama dan harga kamar');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        floor: parseInt(formData.floor),
        price: parseFloat(formData.price)
      };

      if (room?.id) {
        await axios.put(`${API_URL}/api/rooms/${room.id}`, payload);
        toast.success('Kamar berhasil diperbarui');
      } else {
        await axios.post(`${API_URL}/api/rooms`, payload);
        toast.success('Kamar berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan kamar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="name" className="text-sm">Nama Kamar</Label>
          <Input
            id="name"
            name="name"
            placeholder="Contoh: Kamar 101"
            value={formData.name}
            onChange={handleChange}
            className="h-10 bg-muted border-0 rounded-xl"
            data-testid="room-name-input"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="floor" className="text-sm">Lantai</Label>
          <Input
            id="floor"
            name="floor"
            type="number"
            min="1"
            value={formData.floor}
            onChange={handleChange}
            className="h-10 bg-muted border-0 rounded-xl"
            data-testid="room-floor-input"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="price" className="text-sm">Harga per Bulan</Label>
        <CurrencyInput
          id="price"
          name="price"
          placeholder="1.500.000"
          value={formData.price}
          onChange={handleChange}
          className="h-10 bg-muted border-0 rounded-xl"
          data-testid="room-price-input"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Fasilitas</Label>
        <div className="flex flex-wrap gap-2">
          {defaultFacilities.map((facility) => {
            const Icon = facilityIcons[facility] || DoorOpen;
            const isSelected = formData.facilities.includes(facility);
            return (
              <Button
                key={facility}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFacility(facility)}
                className="rounded-full"
                data-testid={`facility-${facility}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {facility}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="description" className="text-sm">Deskripsi (opsional)</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Deskripsi tambahan tentang kamar..."
          value={formData.description}
          onChange={handleChange}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={2}
          data-testid="room-description-input"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Gambar Kamar (opsional)</Label>
        {formData.image_url ? (
          <div className="relative">
            <img
              src={formData.image_url}
              alt="Preview"
              className="w-full h-32 object-cover rounded-xl"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 rounded-full w-7 h-7"
              onClick={removeImage}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-muted-foreground/25 rounded-xl cursor-pointer hover:border-primary/50 transition-colors bg-muted/50">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploadingImage}
            />
            {uploadingImage ? (
              <div className="flex flex-col items-center gap-1">
                <div className="w-6 h-6 spinner" />
                <span className="text-xs text-muted-foreground">Mengupload...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs font-medium">Klik untuk upload gambar</span>
                <span className="text-xs text-muted-foreground">JPG, PNG, WebP (Max 5MB)</span>
              </div>
            )}
          </label>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="room-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : room?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Kamar = () => {
  const { isPemilik } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Gagal memuat data kamar');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (room) => {
    setSelectedRoom(room);
    setDialogOpen(true);
  };

  const handleDelete = (room) => {
    setSelectedRoom(room);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/rooms/${selectedRoom.id}`);
      toast.success('Kamar berhasil dihapus');
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus kamar');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRoom(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedRoom(null);
    fetchRooms();
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="kamar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Kamar</h1>
          <p className="text-muted-foreground mt-1">Kelola semua kamar kost Anda</p>
        </div>
        {isPemilik() && (
          <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-room-btn">
            <Plus className="w-5 h-5 mr-2" />
            Tambah Kamar
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-2xl font-heading font-bold">{rooms.length}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Kamar</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-heading font-bold text-blue-600">
            {rooms.filter(r => r.status === 'terisi').length}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Terisi</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-heading font-bold text-emerald-600">
            {rooms.filter(r => r.status === 'kosong').length}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Kosong</p>
        </Card>
      </div>

      {/* Room Grid */}
      {loading ? (
        <div className="room-grid">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-[4/3]" />
              <div className="p-5 space-y-3">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-full" />
              </div>
            </Card>
          ))}
        </div>
      ) : rooms.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <DoorOpen className="empty-state-icon" />
            <p className="empty-state-title">Belum ada kamar</p>
            <p className="empty-state-description">Tambahkan kamar pertama Anda</p>
            {isPemilik() && (
              <Button onClick={() => setDialogOpen(true)} className="mt-4 rounded-xl">
                <Plus className="w-5 h-5 mr-2" />
                Tambah Kamar
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="room-grid">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedRoom ? 'Edit Kamar' : 'Tambah Kamar Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedRoom ? 'Perbarui informasi kamar' : 'Isi detail kamar yang akan ditambahkan'}
            </DialogDescription>
          </DialogHeader>
          <RoomForm
            room={selectedRoom}
            onSave={handleSave}
            onClose={() => {
              setDialogOpen(false);
              setSelectedRoom(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kamar?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus kamar &quot;{selectedRoom?.name}&quot;? Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-delete-room"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Kamar;
